/**
 * Canonical money math shared FE <-> Nest (Fortress M2.3).
 *
 * ONE VAT formula. Before this module, two forms coexisted and diverged:
 *   - src/lib/contractHelpers.ts paymentVatBreakdown — percent form (rate/100)
 *   - apps/api subscription paths — decimal form (rate 0.15)
 * Both rounded the VAT identically but the duplicated formula was one edit
 * away from drift on a money path.
 *
 * NOTE for apps/api: @dallali/shared is TYPE-only at runtime in the NestJS
 * API (a value import compiles fine but crash-loops the deployed dist —
 * MODULE_NOT_FOUND). The API carries a byte-identical copy in
 * src/finance/vat.util.ts whose parity is pinned by vat.util.spec.ts
 * (specs may value-import this module; they are not part of dist).
 */

export interface VatBreakdown {
  /** Ex-VAT amount (input, clamped to 0 when non-finite/negative). */
  subtotal: number;
  /** ROUND(subtotal x rate / 100, 2) — halala-exact. */
  vat: number;
  /** ROUND(subtotal + vat, 2). */
  total: number;
}

/**
 * Compute a VAT breakdown from an ex-VAT amount and a PERCENT rate (15 = 15%).
 *
 * Mirrors the backend regen-path math in `update_contract_with_invoices`
 * (20260531150000):
 *   vat   = ROUND(amount * rate / 100, 2)
 *   total = ROUND(amount + vat, 2)
 */
export function vatBreakdown(exVatAmount: number, vatRatePercent: number): VatBreakdown {
  const subtotal = Number.isFinite(exVatAmount) && exVatAmount > 0 ? exVatAmount : 0;
  const vat = Math.round((subtotal * vatRatePercent) / 100 * 100) / 100;
  return { subtotal, vat, total: Math.round((subtotal + vat) * 100) / 100 };
}
